import React, { useId, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DatePickerx from "features/datepicker/date-picker";
import type { PickedDocument } from "features/document-tree-picker/DocumentTreeBrowser";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import { isEmptyUploadError } from "services/upload-file.service";
import {
  registerAmendment,
  updateRegisteredAmendment,
  uploadAmendmentFile,
} from "services/contract-amendment.service";
import type { AmendmentChange, RegisteredAmendmentData } from "services/contract-amendment.service";
import AmendmentChangesEditor from "./AmendmentChangesEditor";
import EmploymentTermsFields from "./EmploymentTermsFields";
import {
  AMENDMENT_NUMBER_MAX_LENGTH,
  describeAmendmentError,
  isUploadedScanRefused,
} from "./amendment-errors";
import {
  buildEmploymentTermsPayload,
  changeRowsFromAmendment,
  draftFromAmendment,
  EMPTY_CHANGE_ROW,
  EMPTY_TERMS_DRAFT,
  termsErrorKey,
  withoutEmptyChanges,
} from "./employment-terms";
import type { CurrentTerms, SavedAmendmentTerms } from "./employment-terms";

/** A registered amendment as the list returns it. */
interface RegisteredAmendment extends SavedAmendmentTerms {
  id: string;
  amendmentNumber?: string;
  /** Epoch ms. */
  effectiveDate?: number | null;
  changes?: { point?: string; text?: string; action?: string; isNew?: boolean }[];
  file?: { title?: string; name?: string } | null;
}

interface RegisterAmendmentFormProps {
  companyId: string;
  onboardingId: string;
  employeeUserId?: string;
  currentTerms?: CurrentTerms;
  /** A registered amendment to edit; without it the form registers a new one. */
  amendment?: RegisteredAmendment;
  /** The amendment was registered or saved — the panel closes the form and reloads. */
  onSaved: () => void | Promise<void>;
  onCancel: () => void;
}

type DocumentSource = "upload" | "existing";

/**
 * Registers an old amendment that was already signed on paper: its number,
 * effective date and signed document (a scan uploaded now, or a document that
 * is already in the documents tree), plus (optionally) what it changed,
 * including the structured position / salary / hours terms. With `amendment`
 * it edits the number, date, changes and terms of an already registered one;
 * its document cannot be replaced.
 */
export default function RegisterAmendmentForm({
  companyId,
  onboardingId,
  employeeUserId,
  currentTerms,
  amendment,
  onSaved,
  onCancel,
}: RegisterAmendmentFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const numberInputId = useId();
  const sourceGroupName = useId();
  const [amendmentNumber, setAmendmentNumber] = useState(amendment?.amendmentNumber ?? "");
  const [effectiveDate, setEffectiveDate] = useState<number | null>(amendment?.effectiveDate ?? null);
  // Only one of the two is ever set: switching the source clears the other.
  const [documentSource, setDocumentSource] = useState<DocumentSource>("upload");
  const [scan, setScan] = useState<File | null>(null);
  const [pickedDocument, setPickedDocument] = useState<PickedDocument | null>(null);
  const [changes, setChanges] = useState<AmendmentChange[]>(
    amendment ? changeRowsFromAmendment(amendment) : [EMPTY_CHANGE_ROW]
  );
  const [termsDraft, setTermsDraft] = useState(amendment ? draftFromAmendment(amendment) : EMPTY_TERMS_DRAFT);
  const [saving, setSaving] = useState(false);
  // The chosen scan is uploaded once; a retry after a refused registration
  // reuses it instead of leaving another unused file behind.
  const uploadedFileId = useRef<string | null>(null);

  const chooseDocumentSource = (source: DocumentSource) => {
    setDocumentSource(source);
    setScan(null);
    setPickedDocument(null);
    uploadedFileId.current = null;
  };

  // The picker is a modalx dialog: options, component, then props (the component resets the props).
  const openDocumentPicker = () => {
    dispatch(setModalxOptions({ title: t("select_existing_document"), size: "large" }));
    dispatch(setModalxComponent("DocumentTreePickerModal"));
    dispatch(setModalxComponentProps({ companyId, onPick: setPickedDocument }));
  };

  /** What both the register and the edit request send; null after telling the user what is missing. */
  const readBody = (): RegisteredAmendmentData | null => {
    if (!amendmentNumber.trim()) {
      toast.error(t("amendment_number_required"));
      return null;
    }
    if (effectiveDate === null) {
      toast.error(t("amendment_effective_date_required"));
      return null;
    }
    const termsError = termsErrorKey(termsDraft);
    if (termsError) {
      toast.error(t(termsError));
      return null;
    }
    return {
      amendmentNumber: amendmentNumber.trim(),
      effectiveDate,
      changes: withoutEmptyChanges(changes),
      ...buildEmploymentTermsPayload(termsDraft),
    };
  };

  /** Runs a save with the buttons disabled; a refusal is explained and the form stays open. */
  const runSave = async (save: () => Promise<void>) => {
    setSaving(true);
    try {
      await save();
      await onSaved();
    } catch (err: any) {
      // The refused file must be uploaded afresh on the next try.
      if (isUploadedScanRefused(err)) uploadedFileId.current = null;
      // An empty file is already toasted (translated) by the upload service.
      if (!isEmptyUploadError(err)) toast.error(describeAmendmentError(err, t));
    } finally {
      setSaving(false);
    }
  };

  const uploadScanOnce = async (file: File) => {
    const fileId = uploadedFileId.current ?? (await uploadAmendmentFile(file, companyId));
    uploadedFileId.current = fileId;
    return fileId;
  };

  const handleSubmit = async () => {
    const body = readBody();
    if (!body) return;

    if (amendment) {
      await runSave(async () => {
        await updateRegisteredAmendment(companyId, amendment.id, body);
        toast.success(t("amendment_updated"));
      });
      return;
    }

    const register = async (documentField: { fileId: string } | { existingFileId: string }) => {
      await registerAmendment(companyId, onboardingId, { ...body, ...documentField });
      toast.success(t("amendment_registered"));
    };

    if (documentSource === "upload") {
      if (!scan) {
        toast.error(t("amendment_scan_required"));
        return;
      }
      await runSave(async () => register({ fileId: await uploadScanOnce(scan) }));
      return;
    }
    if (!pickedDocument) {
      toast.error(t("amendment_scan_required"));
      return;
    }
    await runSave(() => register({ existingFileId: pickedDocument.id }));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
      <h3 className="text-base font-semibold mb-4">
        {amendment ? t("amendment_edit_registered") : t("amendment_register_signed")}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor={numberInputId} className="block text-sm font-medium text-gray-700 mb-1">
            {t("amendment_number")} *
          </label>
          <input
            id={numberInputId}
            type="text"
            maxLength={AMENDMENT_NUMBER_MAX_LENGTH}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={amendmentNumber}
            onChange={(e) => setAmendmentNumber(e.target.value)}
          />
        </div>
        <div>
          <DatePickerx
            title={t("effective_date")}
            selected={effectiveDate === null ? "" : new Date(effectiveDate).toISOString().split("T")[0]}
            onChange={(date: Date | null) => setEffectiveDate(date ? date.getTime() : null)}
            isRequired
            disableDatesAfter={null}
          />
        </div>
      </div>

      {amendment ? (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">{t("amendment_signed_scan")}</p>
          <p className="text-sm text-gray-600">{amendment.file?.title || amendment.file?.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t("amendment_document_locked")}</p>
        </div>
      ) : (
        <fieldset className="mb-4">
          <legend className="text-sm font-medium text-gray-700 mb-1">{t("amendment_signed_scan")} *</legend>
          <div className="flex flex-wrap gap-4 mb-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={sourceGroupName}
                checked={documentSource === "upload"}
                onChange={() => chooseDocumentSource("upload")}
              />
              {t("amendment_document_upload")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="radio"
                name={sourceGroupName}
                checked={documentSource === "existing"}
                onChange={() => chooseDocumentSource("existing")}
              />
              {t("amendment_document_from_tree")}
            </label>
          </div>
          {documentSource === "upload" ? (
            <div className="flex items-center gap-2">
              <label className="px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 text-sm cursor-pointer whitespace-nowrap max-md:min-h-11 max-md:inline-flex max-md:items-center">
                {t("choose_file")}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  onChange={(e) => {
                    uploadedFileId.current = null;
                    setScan(e.target.files?.[0] ?? null);
                  }}
                />
              </label>
              {scan && <span className="text-sm text-gray-600 truncate">{scan.name}</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 text-sm whitespace-nowrap max-md:min-h-11"
                onClick={openDocumentPicker}
              >
                {pickedDocument ? t("amendment_document_change") : t("select_existing_document")}
              </button>
              {pickedDocument && <span className="text-sm text-gray-600 truncate">{pickedDocument.title}</span>}
            </div>
          )}
        </fieldset>
      )}

      <AmendmentChangesEditor changes={changes} onChange={setChanges} termsDraft={termsDraft} />
      <EmploymentTermsFields
        companyId={companyId}
        employeeUserId={employeeUserId}
        currentTerms={currentTerms}
        draft={termsDraft}
        onChange={setTermsDraft}
        warnAboutOldPositions
      />

      <div className="flex gap-2">
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "..." : t("save")}
        </button>
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 max-md:min-h-11"
          onClick={onCancel}
          disabled={saving}
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}
